/**
 * Maps an order-fetch failure onto a diner-facing state.
 *
 * The BFF's `get_order` never explains *why* it refused — by design. It has
 * three auth legs (customer JWT, group participant token, dine-in payer
 * token) and:
 *   - no usable identity at all              → 401 "Could not validate credentials"
 *   - a payer token bound to a DIFFERENT order → 401 (same string)
 *   - signed in, but not this order's customer → 404 (deliberately masked)
 *   - the order genuinely doesn't exist        → 404
 *
 * Raw backend strings must never reach a diner, so screens branch on this
 * classification instead of rendering `error.message`.
 */
import { ApiError } from "@/api/http";

export type OrderErrorKind =
  /** 401 — we have no identity the BFF accepts for this order. */
  | "identity"
  /** 404 — the order exists for someone else, or not at all. */
  | "not_yours"
  /** Network / 5xx / anything else — retryable. */
  | "generic";

export function classifyOrderError(err: unknown): OrderErrorKind {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return "identity";
    if (err.status === 404) return "not_yours";
  }
  return "generic";
}
